import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import subjectAndGroupingApis from '../../apis/subjects';
import DropdownSelect from '../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../enums';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import { Subject } from './types';
import SubjectTableAndActions from './components/SubjectTableAndActions';
import AddSubjectModal from './components/AddSubjectModal';
import EditSubjectModal from './components/EditSubjectModal';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { StyledButton } from '../Exams/styled';
import { ButtonBox, SearchContainer, SearchItemContainer } from './styled';
import { PageWrapper, Title } from '../common/styled';
import Icons from '../../components/icons';

interface SubjectAndGroupingPageProps {
  allClasses?: SchoolClassesConfig;
  school: string;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const SubjectPage: React.FC<SubjectAndGroupingPageProps> = ({
  allClasses,
  school,
  onShowToast
}) => {
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingSubjectId, setDeletingSubjectId] = useState<string | null>(
    null
  );
  const [isSearched, setIsSearched] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);

  const handleApiSuccessAndFailure = (isSuccess: boolean, message: string) => {
    setIsAddModalOpen(false);
    handleEditCloseModal();
    onShowToast(
      message,
      isSuccess ? SnackbarSeverityType.success : SnackbarSeverityType.error
    );
    handleSearch();
  };

  useEffect(() => {
    if (allClasses) {
      setGradeOptions(allClasses.grades || []);
    }
  }, [allClasses]);

  useEffect(() => {
    if (allClasses && grade) {
      const divisions = allClasses.classes
        .filter(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.grade === grade
        )
        .map(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.division
        );
      setDivisionOptions(divisions);
    } else if (allClasses) {
      setDivisionOptions(allClasses.divisions || []);
    }
  }, [allClasses, grade]);

  const handleSearch = async () => {
    if (grade && division && school) {
      setIsLoading(true);
      setIsSearched(false);
      try {
        const response: any = await subjectAndGroupingApis.getSubjects(
          school,
          grade,
          division
        );
        const apiSubjects = Array.isArray(response) ? response : [];
        setSubjects(apiSubjects);
      } catch (error) {
        console.error(error);
        setSubjects([]);
        onShowToast('Failed to load subjects.', SnackbarSeverityType.error);
      } finally {
        setIsLoading(false);
        setIsSearched(true);
      }
    } else {
      setSubjects([]);
    }
  };

  const handleEditOpenModal = (subject: Subject) => {
    setSelectedSubject(subject);
    setIsEditModalOpen(true);
  };

  const handleEditCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedSubject(null);
  };

  const handleDeleteClick = (subject: Subject) => {
    setSelectedSubject(subject);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedSubject(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubject?.id) {
      onShowToast(
        'No subject selected for deletion.',
        SnackbarSeverityType.error
      );
      handleDeleteCancel();
      return;
    }
    setDeletingSubjectId(selectedSubject.id);
    try {
      await subjectAndGroupingApis.deleteSubject(school, selectedSubject.id);
      onShowToast(
        'Subject deleted successfully!',
        SnackbarSeverityType.success
      );
      handleSearch();
    } catch (error: any) {
      console.error('Error deleting subject:', error);
      onShowToast(
        error?.errors?.[0].message || 'Failed to delete subject.',
        SnackbarSeverityType.error
      );
    } finally {
      setDeletingSubjectId(null);
      handleDeleteCancel();
    }
  };

  const handleGradeSearchChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setGrade(event.target.value as string);
    setDivision('');
    setDivisionOptions([]);
    setSubjects([]);
    setIsSearched(false);
  };

  const divisionOption = divisionOptions.map((d) => ({ value: d, label: d }));

  return (
    <>
      <Title>Subjects Management</Title>
      <Box>
        <PageWrapper>
          <SearchContainer>
            <SearchItemContainer>
              <DropdownSelect
                label='Grade'
                name='grade'
                value={grade}
                options={gradeOptions.map((g) => ({ value: g, label: g }))}
                placeholder='Select Grade'
                onChange={handleGradeSearchChange}
              />
            </SearchItemContainer>
            <SearchItemContainer>
              <DropdownSelect
                label='Division'
                name='division'
                value={division}
                options={divisionOption}
                onChange={(e) => {
                  setDivision(e.target.value as string);
                }}
                placeholder='Select Division'
              />
            </SearchItemContainer>
            <ButtonBox>
              <StyledButton
                onClick={handleSearch}
                disabled={!grade || !division || isLoading}
              >
                <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
                {isLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Search'
                )}
              </StyledButton>
            </ButtonBox>
          </SearchContainer>
        </PageWrapper>

        {grade && division && isSearched && (
          <SubjectTableAndActions
            subjects={subjects}
            isLoading={isLoading}
            onEdit={handleEditOpenModal}
            onDelete={handleDeleteClick}
            onAddSubject={setIsAddModalOpen}
          />
        )}

        <AddSubjectModal
          fetchData={handleSearch}
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
          divisionOptions={divisionOption}
          grade={grade}
        />

        <EditSubjectModal
          fetchData={handleSearch}
          open={isEditModalOpen}
          onClose={handleEditCloseModal}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
          subject={selectedSubject}
          grade={grade}
        />

        <ConfirmationDialog
          open={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete "${
            selectedSubject?.name || ''
          }"?`}
          disable={deletingSubjectId !== null}
        />
      </Box>
    </>
  );
};

export default SubjectPage;
